import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { AlertCircle, Eye, EyeOff, Check } from 'lucide-react';
import { usersApi } from '../../services/usersApi';
import { mapFarms } from '../../utils/mapMockData';
import './UserFormModal.css';

export function UserFormModal({ isOpen, onClose, onSuccess, user }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'viewer',
    farms: [],
    customPermissions: {
      pages: [],
      analyticsPermissions: {
        downloadData: false,
      },
      reportsPermissions: {
        viewReports: false,
        downloadDocuments: false,
      },
    },
    password: '',
    isActive: true,
  });

  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Available pages for custom role
  const availablePages = [
    { id: 'dashboard', label: t('nav.dashboard') },
    { id: 'analytics', label: t('nav.analytics') },
    { id: 'map', label: t('nav.map') },
    { id: 'stations', label: t('nav.stations') },
    { id: 'reports', label: t('nav.reports') },
    { id: 'settings', label: t('nav.settings') },
    { id: 'users', label: t('nav.users') },
  ];

  const isEditMode = !!user;

  // Load user data when editing
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        role: user.role || 'viewer',
        farms: user.farms || [],
        customPermissions: user.customPermissions || {
          pages: [],
          analyticsPermissions: {
            downloadData: false,
          },
          reportsPermissions: {
            viewReports: false,
            downloadDocuments: false,
          },
        },
        password: '', // Don't prefill password
        isActive: user.isActive,
      });
      setConfirmPassword('');
    } else {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'viewer',
        farms: [],
        customPermissions: {
          pages: [],
          analyticsPermissions: {
            downloadData: false,
          },
          reportsPermissions: {
            viewReports: false,
            downloadDocuments: false,
          },
        },
        password: '',
        isActive: true,
      });
      setConfirmPassword('');
    }
    setErrors({});
    setSubmitError('');
  }, [user, isOpen]);

  const validate = () => {
    const newErrors = {};

    if (!formData.email?.trim()) {
      newErrors.email = t('validation.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.invalidEmail');
    }

    if (!formData.firstName?.trim()) {
      newErrors.firstName = t('validation.required');
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = t('validation.required');
    }

    // Farm validation
    if (!formData.farms || formData.farms.length === 0) {
      newErrors.farms = t('users.validation.farmsRequired');
    }

    // Custom role validation
    if (formData.role === 'custom') {
      if (!formData.customPermissions.pages || formData.customPermissions.pages.length === 0) {
        newErrors.customPages = t('users.validation.customPagesRequired');
      }
    }

    // Password validation (only for create or if password is provided)
    if (!isEditMode) {
      if (!formData.password || formData.password.length < 8) {
        newErrors.password = t('validation.passwordTooShort');
      } else if (formData.password !== confirmPassword) {
        newErrors.confirmPassword = t('validation.passwordsDoNotMatch');
      }
    } else if (formData.password) {
      // Editing and password provided
      if (formData.password.length < 8) {
        newErrors.password = t('validation.passwordTooShort');
      } else if (formData.password !== confirmPassword) {
        newErrors.confirmPassword = t('validation.passwordsDoNotMatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      // For edit mode, only send password if it was changed
      const dataToSubmit = { ...formData };
      if (isEditMode && !dataToSubmit.password) {
        delete dataToSubmit.password;
      }

      if (isEditMode && user) {
        await usersApi.update(user.id, dataToSubmit);
      } else {
        await usersApi.create(dataToSubmit);
      }
      onSuccess();
      onClose();
    } catch (error) {
      setSubmitError(error.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return null;
    if (password.length < 6) return 'weak';
    if (password.length < 10) return 'medium';
    if (password.length >= 10 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) {
      return 'strong';
    }
    return 'medium';
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const footer = (
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
      <Button
        type="button"
        variant="secondary"
        onClick={onClose}
        disabled={loading}
      >
        {t('users.modals.addUser.buttons.cancel')}
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={(e) => {
          e.preventDefault();
          const form = document.getElementById('user-form');
          if (form) {
            handleSubmit(e);
          }
        }}
        disabled={loading}
      >
        {loading ? t('users.modals.addUser.buttons.saving') : 
          (isEditMode ? t('users.modals.addUser.buttons.saveChanges') : t('users.modals.addUser.buttons.save'))}
      </Button>
    </div>
  );

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="md"
      title={isEditMode ? t('users.modals.addUser.editTitle') : t('users.modals.addUser.title')}
      footer={footer}
    >
      <div className="user-form-modal">
        {/* Error Banner */}
        {submitError && (
          <div className="form-error-banner">
            <AlertCircle size={20} />
            <span>{submitError}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="user-form" id="user-form">
          <div className="form-section">
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">
                  {t('users.modals.addUser.fields.firstName.label')} <span className="required">*</span>
                </label>
                <Input
                  value={formData.firstName || ''}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder={t('users.modals.addUser.fields.firstName.placeholder')}
                  disabled={loading}
                />
                {errors.firstName && (
                  <div className="form-error">{errors.firstName}</div>
                )}
              </div>

              <div className="form-field">
                <label className="form-label">
                  {t('users.modals.addUser.fields.lastName.label')} <span className="required">*</span>
                </label>
                <Input
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder={t('users.modals.addUser.fields.lastName.placeholder')}
                  disabled={loading}
                />
                {errors.lastName && (
                  <div className="form-error">{errors.lastName}</div>
                )}
              </div>
            </div>

            <div className="form-field">
              <label className="form-label">
                {t('users.modals.addUser.fields.email.label')} <span className="required">*</span>
              </label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('users.modals.addUser.fields.email.placeholder')}
                disabled={loading}
              />
              {errors.email && (
                <div className="form-error">{errors.email}</div>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">
                {t('users.modals.addUser.fields.phone.label')}
              </label>
              <Input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t('users.modals.addUser.fields.phone.placeholder')}
                disabled={loading}
              />
            </div>

            {/* Farm Selection */}
            <div className="form-field">
              <label className="form-label">
                {t('users.modals.viewUser.sections.farmAccess')} <span className="required">*</span>
              </label>
              <div className="farm-select-container">
                {mapFarms.map((farm) => (
                  <label key={farm.id} className="farm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.farms.includes(farm.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            farms: [...formData.farms, farm.id],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            farms: formData.farms.filter((id) => id !== farm.id),
                          });
                        }
                      }}
                      disabled={loading}
                    />
                    <span>{farm.name}</span>
                  </label>
                ))}
              </div>
              {errors.farms && (
                <div className="form-error">{errors.farms}</div>
              )}
            </div>

            {/* Role Selection */}
            <div className="form-field">
              <label className="form-label">
                {t('users.modals.addUser.fields.role.label')} <span className="required">*</span>
              </label>
              <select
                className="form-select"
                value={formData.role || 'viewer'}
                onChange={(e) => {
                  const newRole = e.target.value;
                  setFormData({
                    ...formData,
                    role: newRole,
                    // Reset custom permissions if switching away from custom
                    customPermissions: newRole === 'custom' ? formData.customPermissions : {
                      pages: [],
                      analyticsPermissions: { downloadData: false },
                      reportsPermissions: { viewReports: false, downloadDocuments: false },
                    },
                  });
                }}
                disabled={loading}
              >
                <option value="admin">{t('users.table.roles.admin')}</option>
                <option value="reviewer">{t('users.table.roles.reviewer')}</option>
                <option value="viewer">{t('users.table.roles.viewer')}</option>
                <option value="custom">{t('users.table.roles.custom')}</option>
              </select>
            </div>

            {/* Custom Permissions Section */}
            {formData.role === 'custom' && (
              <div className="custom-permissions-section">
                <div className="form-field">
                  <label className="form-label">
                    {t('users.modals.viewUser.fields.pageAccess')} <span className="required">*</span>
                  </label>
                  <div className="pages-checkbox-container">
                    {availablePages.map((page) => (
                      <label key={page.id} className="page-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.customPermissions.pages.includes(page.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                customPermissions: {
                                  ...formData.customPermissions,
                                  pages: [...formData.customPermissions.pages, page.id],
                                },
                              });
                            } else {
                              setFormData({
                                ...formData,
                                customPermissions: {
                                  ...formData.customPermissions,
                                  pages: formData.customPermissions.pages.filter((id) => id !== page.id),
                                  // Remove analytics permissions if analytics page is unchecked
                                  analyticsPermissions: page.id === 'analytics' 
                                    ? { downloadData: false }
                                    : formData.customPermissions.analyticsPermissions,
                                  // Remove reports permissions if reports page is unchecked
                                  reportsPermissions: page.id === 'reports'
                                    ? { viewReports: false, downloadDocuments: false }
                                    : formData.customPermissions.reportsPermissions,
                                },
                              });
                            }
                          }}
                          disabled={loading}
                        />
                        <span>{page.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.customPages && (
                    <div className="form-error">{errors.customPages}</div>
                  )}
                </div>

                {/* Analytics Permissions */}
                {formData.customPermissions.pages.includes('analytics') && (
                  <div className="form-field sensitive-permissions">
                    <label className="form-label">{t('users.modals.viewUser.fields.analyticsPermissions')}</label>
                    <div className="permissions-checkbox-container">
                      <label className="permission-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.customPermissions.analyticsPermissions.downloadData}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              customPermissions: {
                                ...formData.customPermissions,
                                analyticsPermissions: {
                                  downloadData: e.target.checked,
                                },
                              },
                            });
                          }}
                          disabled={loading}
                        />
                        <span>{t('users.modals.viewUser.permissions.downloadData')}</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Reports Permissions */}
                {formData.customPermissions.pages.includes('reports') && (
                  <div className="form-field sensitive-permissions">
                    <label className="form-label">{t('users.modals.viewUser.fields.reportsPermissions')}</label>
                    <div className="permissions-checkbox-container">
                      <label className="permission-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.customPermissions.reportsPermissions.viewReports}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              customPermissions: {
                                ...formData.customPermissions,
                                reportsPermissions: {
                                  ...formData.customPermissions.reportsPermissions,
                                  viewReports: e.target.checked,
                                },
                              },
                            });
                          }}
                          disabled={loading}
                        />
                        <span>{t('users.modals.viewUser.permissions.viewReports')}</span>
                      </label>
                      <label className="permission-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.customPermissions.reportsPermissions.downloadDocuments}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              customPermissions: {
                                ...formData.customPermissions,
                                reportsPermissions: {
                                  ...formData.customPermissions.reportsPermissions,
                                  downloadDocuments: e.target.checked,
                                },
                              },
                            });
                          }}
                          disabled={loading}
                        />
                        <span>{t('users.modals.viewUser.permissions.downloadDocuments')}</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Password Fields */}
            <div className="form-field">
              <label className="form-label">
                {t('users.modals.addUser.fields.password.label')} {!isEditMode && <span className="required">*</span>}
              </label>
              <div className="password-input-wrapper">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <div className="form-error">{errors.password}</div>
              )}
              {isEditMode && (
                <div className="form-helper">
                  {t('users.modals.addUser.fields.password.helper')}
                </div>
              )}
              {!isEditMode && formData.password && (
                <div className="password-strength">
                  <div className="password-strength-bar">
                    <div
                      className={`password-strength-fill password-strength-fill--${passwordStrength}`}
                      style={{ width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'medium' ? '66%' : '100%' }}
                    />
                  </div>
                  <div className="password-strength-label">
                    {passwordStrength === 'weak' && t('users.modals.addUser.passwordStrength.weak')}
                    {passwordStrength === 'medium' && t('users.modals.addUser.passwordStrength.medium')}
                    {passwordStrength === 'strong' && t('users.modals.addUser.passwordStrength.strong')}
                  </div>
                </div>
              )}
            </div>

            {(!isEditMode || formData.password) && (
              <div className="form-field">
                <label className="form-label">
                  {t('users.modals.addUser.fields.confirmPassword.label')} <span className="required">*</span>
                </label>
                <div className="password-input-wrapper">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword || ''}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="form-error">{errors.confirmPassword}</div>
                )}
              </div>
            )}

            <div className="form-field">
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  disabled={loading}
                />
                <span>{t('users.modals.addUser.fields.status.activeLabel')}</span>
              </label>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
